/**
 * UTILITY DATA-REQUEST EMAIL — turns an undocumented site-envelope limit into
 * a ready-to-send counterparty request (gas utility / pipeline, water utility,
 * CO₂ offtaker). This module only COMPOSES text: the user reviews and sends
 * the email from their own mail client (mailto:) or pastes the copied draft.
 * Nothing is ever sent automatically.
 *
 * The ask is the solver's computed firm demand plus a nomination planning
 * margin. That margin is deliberately NOT the solver's capacity reserve
 * (DEFAULT_RESERVE_MARGIN_PCT): the capacity reserve is already embodied in
 * the fleet the peak was computed from, so reusing it here would double-count.
 * The draft always states both the raw computed figure and the margin applied.
 *
 * The "evidence to return" lists mirror `directDocumentFactIsQualified` in
 * screeningEvidencePrefill.ts — the request asks for exactly the document the
 * evidence resolver will later accept (gas/CO₂: firm AND site-deliverable;
 * water: a consumption basis, never withdrawal-only).
 *
 * Pure + deterministic: same context → same draft.
 */
import type {
  GenerationImpactResult,
  GenerationImpactScenarioInput,
} from '../../types/generationImpact';
import type { OemDataRequestEmail } from './oemDataRequestEmail';
import { deriveGasDemandPerspective } from './screeningIntegrity';

export type UtilityResourceKind = 'gas' | 'water' | 'co2';

/**
 * Default nomination planning margin applied on top of the computed firm
 * demand when drafting the ask. Editable per request in the UI; a planning
 * cushion for the nomination conversation, not an engineering reserve.
 */
export const DEFAULT_NOMINATION_MARGIN_PCT = 10;

export interface UtilityAsk {
  kind: UtilityResourceKind;
  /** Counterparty category label ("gas utility / pipeline", …). */
  counterpartyLabel: string;
  /**
   * How the commitment is named in this resource's own trade language, e.g.
   * "firm" for pipeline capacity but "committed supply" for a water
   * will-serve. Never call a water commitment "firm" — that is gas vocabulary
   * and disagrees with the consumption basis the draft actually requests.
   */
  commitmentTerm: string;
  /** Solver-quoted computed demand (firm peak / total consumption / captured t/y). */
  demandValue: number;
  demandUnit: string;
  /** demandValue × (1 + nominationMarginPct/100), rounded up to a whole unit. */
  askValue: number;
  nominationMarginPct: number;
  /** Screening basis lines for the email body (load, hours, technology basis). */
  basisLines: readonly string[];
  /** Peak-vs-average or hours-conditionality framing; null when none applies. */
  perspectiveNote: string | null;
  /** What the counterparty must send back for the figure to become screenable evidence. */
  evidenceToReturn: readonly string[];
  /** Present when the demand figure itself is incomplete (water: missing cooling basis). */
  incomplete?: string;
}

const COUNTERPARTY_LABELS: Record<UtilityResourceKind, string> = {
  gas: 'gas utility / pipeline',
  water: 'water utility',
  co2: 'CO₂ transport & storage offtaker',
};

const askNumber = (value: number): string =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);

/**
 * Derive the per-resource ask from an already-solved scenario. Returns null
 * when the resource is not applicable to this scenario (no gas demand, CCS
 * disabled) — mirroring the constraint-fit `not-applicable` states.
 */
export function deriveUtilityAsk(args: {
  kind: UtilityResourceKind;
  input: GenerationImpactScenarioInput;
  result: GenerationImpactResult;
  nominationMarginPct?: number;
}): UtilityAsk | null {
  const marginPct = args.nominationMarginPct ?? DEFAULT_NOMINATION_MARGIN_PCT;
  const withMargin = (demand: number): number => Math.ceil(demand * (1 + marginPct / 100));
  const { input, result } = args;
  const basisLines = [
    `Facility load ${askNumber(input.facilityLoadMW)} MW; annual operating basis ${askNumber(input.annualOperatingHours)} h/yr (full-output capability).`,
    `On-site generation screening basis: ${askNumber(result.capacity.normalNetMW)} MW normal net across ${result.unitResults.length} unit group${result.unitResults.length === 1 ? '' : 's'}.`,
  ];

  if (args.kind === 'gas') {
    const demand = result.gas.peakDthPerDay;
    if (!Number.isFinite(demand) || demand <= 0) return null;
    const normalNet = result.capacity.normalNetMW;
    const facilityLoadShare = Number.isFinite(normalNet) && normalNet > 0 && input.facilityLoadMW >= 0
      ? input.facilityLoadMW / normalNet
      : null;
    const perspective = deriveGasDemandPerspective({
      peakDthPerDay: demand,
      annualMmbtu: result.gas.annualMmbtu,
      facilityLoadShare,
    });
    return {
      kind: 'gas',
      counterpartyLabel: COUNTERPARTY_LABELS.gas,
      commitmentTerm: 'firm',
      demandValue: demand,
      demandUnit: 'Dth/day',
      askValue: withMargin(demand),
      nominationMarginPct: marginPct,
      basisLines,
      perspectiveNote: perspective?.note ?? null,
      evidenceToReturn: [
        'A firm, site-deliverable capacity commitment stating the deliverable Dth/day at the site meter (contract or precedent-agreement letter) — interruptible or hub-level figures cannot be screened.',
        'The guaranteed minimum delivery pressure at the site meter, which drives the interconnect derate screen.',
        'Station design capacity and any seasonal or maintenance derates that apply to the firm figure.',
      ],
    };
  }

  if (args.kind === 'water') {
    const total = result.water.totalConsumptionGalPerDay;
    const fallback = result.water.generationConsumptionGalPerDay + result.water.ccsConsumptionGalPerDay;
    const demand = total ?? fallback;
    if (!Number.isFinite(demand) || demand <= 0) return null;
    return {
      kind: 'water',
      counterpartyLabel: COUNTERPARTY_LABELS.water,
      commitmentTerm: 'committed consumptive supply',
      demandValue: demand,
      demandUnit: 'gal/day',
      askValue: withMargin(demand),
      nominationMarginPct: marginPct,
      basisLines,
      perspectiveNote: 'The figure is peak-day consumptive use; annual volumes follow the served operating profile and will be lower.',
      evidenceToReturn: [
        'A supply commitment on a CONSUMPTION basis in gal/day (a will-serve or capacity letter). A withdrawal-only figure cannot be screened as consumption capacity.',
        'For our records (not screened here): the permitted withdrawal capacity and the source (surface / groundwater / reclaimed).',
      ],
      ...(total == null
        ? { incomplete: 'This ask covers generation and carbon-capture water only — the facility cooling basis is not yet set, so the campus total will be higher.' }
        : {}),
    };
  }

  const captured = result.emissions.capturedCo2TonnesPerYear;
  if (!input.ccs.enabled || !Number.isFinite(captured) || captured <= 0) return null;
  return {
    kind: 'co2',
    counterpartyLabel: COUNTERPARTY_LABELS.co2,
    commitmentTerm: 'firm',
    demandValue: captured,
    demandUnit: 't/y',
    askValue: withMargin(captured),
    nominationMarginPct: marginPct,
    basisLines,
    perspectiveNote: `The annual figure assumes ${askNumber(input.annualOperatingHours)} full-load hours per year; the served operating profile will deliver less.`,
    evidenceToReturn: [
      'A firm, site-deliverable annual export capacity commitment in metric tonnes per year at the site battery limit.',
      'Confirmation of the transport route and injection/storage capacity (route confirmation unlocks the export screen).',
    ],
  };
}

export interface UtilityDataRequestContext {
  ask: UtilityAsk;
  siteName: string;
  /** Known contact; empty string leaves the To: field for the user. */
  contactEmail?: string;
}

/** Some mail clients truncate long mailto bodies; keep the draft comfortably under that. */
const MAILTO_BODY_LIMIT = 1_800;

const RESOURCE_SUBJECT: Record<UtilityResourceKind, string> = {
  gas: 'Firm gas capacity request',
  water: 'Water supply capacity request',
  co2: 'CO₂ export capacity request',
};

export function buildUtilityDataRequestEmail(context: UtilityDataRequestContext): OemDataRequestEmail {
  const { ask } = context;
  const to = context.contactEmail?.trim() ?? '';
  const subject = `${RESOURCE_SUBJECT[ask.kind]} — ${context.siteName}`;

  const evidenceLines = ask.evidenceToReturn
    .map((item, index) => `${index + 1}. ${item}`)
    .join('\n');

  const body = [
    'Dear team,',
    '',
    `We are developing on-site power generation at our ${context.siteName} project and request a capacity commitment from the ${ask.counterpartyLabel}.`,
    '',
    `Requested capacity: ${askNumber(ask.askValue)} ${ask.demandUnit} (computed firm requirement ${askNumber(ask.demandValue)} ${ask.demandUnit} plus a ${ask.nominationMarginPct}% nomination planning margin).`,
    '',
    'Screening basis:',
    ...ask.basisLines.map((line) => `- ${line}`),
    ...(ask.perspectiveNote ? ['', ask.perspectiveNote] : []),
    ...(ask.incomplete ? ['', `Note: ${ask.incomplete}`] : []),
    '',
    'To carry the figure into our engineering screen we need the response to state:',
    '',
    evidenceLines,
    '',
    'We are happy to execute an NDA or a study agreement to receive project-grade documentation. Please direct the response, or an introduction to the appropriate capacity/interconnection contact, to this address.',
    '',
    'Kind regards,',
  ].join('\n');

  const truncatedBody = body.length > MAILTO_BODY_LIMIT
    ? `${body.slice(0, MAILTO_BODY_LIMIT - 1)}…`
    : body;
  const mailtoHref = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(truncatedBody)}`;

  return { to, subject, body, mailtoHref };
}
